import { encode, toGeoJSON } from '@mapbox/polyline';
import { area, Feature, Geometry, lineString, lineToPolygon, Polygon } from '@turf/turf';
import { Entity } from 'dexie';
import formatAddress from '../../utils/format-address';
import { getFormatE164, getFormatNational } from '../../utils/normalize-phone';
import type { AppDB } from '../db';

// Kept outside the instance: an own property on the entity would be persisted by Dexie
const decodedPolygons = new WeakMap<Landmark, { geom: string; polygon: Feature<Polygon> }>();
const areas = new WeakMap<Feature<Geometry>, number>();

class Landmark extends Entity<AppDB> {
  public address1!: string | null;
  public address2!: string;
  public address3!: string;
  public alarms!: number[];
  public city!: string | null;
  public color!: number | null;
  public comment!: string;
  public country!: number;
  public devices!: number[];
  public fax!: string;
  public firstname!: string;
  public icon!: number;
  public keywords!: string;
  public kind!: 'circle' | 'point' | 'polygon';
  public label!: string;
  public lastname!: string;
  public latitude!: number;
  public longitude!: number;
  public mail!: string;
  public phone!: string;
  public postal_code!: string | null;
  public radius!: number | null;
  public state!: 'V' | 'W'; // V for Validate, W for Waiting. Landmark created by drive app.
  public street!: string;
  public timestamp!: number;
  public uid_parent!: number;
  public uid_picture!: number | null;
  public uid_service!: number | null;
  public temp_picture!: File | null;
  public uid!: number;
  public feature!: Feature<Geometry>;
  public display!: boolean;
  public geom!: string;

  public get address() {
    return formatAddress({
      address1: this.address1,
      city: this.city,
      longitude: this.longitude,
      latitude: this.latitude,
      postal_code: this.postal_code,
    });
  }

  public get hexaColor() {
    const color = this.color || 0;
    return (
      '#' +
      [color & 255, (color >> 8) & 255, (color >> 16) & 255]
        .map(channel => channel.toString(16).padStart(2, '0'))
        .join('')
    );
  }

  public get displayOnMap() {
    return this.display === undefined ? true : this.display;
  }

  public get polygon() {
    if ((this.geom || this.feature) && (this.kind === 'polygon' || this.kind === 'circle')) {
      // TODO A mettre dans une classe utilitaire, utilisé également dans Landmark.ts
      if (this.feature) return this.feature as any;
      const cached = decodedPolygons.get(this);
      if (cached?.geom === this.geom) return cached.polygon;
      const geometry = toGeoJSON(this.geom);
      const line = lineString(geometry.coordinates);

      const polygon = lineToPolygon(line);
      decodedPolygons.set(this, { geom: this.geom, polygon });

      return polygon;
    }

    return null;
  }

  public get area() {
    const polygon = this.polygon;
    if (!polygon) return null;

    let value = areas.get(polygon);
    if (value === undefined) {
      value = Math.trunc(area(polygon));
      areas.set(polygon, value);
    }

    return value;
  }

  // TODO : legacy class variable, maybe change it later
  public get keywordList() {
    return !!this.keywords && this.keywords !== '' ? this.keywords.split(',').map(Number) : [];
  }

  get fullname() {
    const firstname = this.firstname?.trim() === '' ? null : this.firstname;
    const lastname = this.lastname?.trim() === '' ? null : this.lastname;
    if (firstname && lastname) {
      return `${firstname} ${lastname}`;
    }
    return firstname || lastname || '';
  }

  public get formattedAddress() {
    return this.street
      ? `${this.street}, ${this.postal_code} ${this.city}`
      : this.address1
        ? `${this.address1}, ${this.postal_code} ${this.city}`
        : `${this.postal_code} ${this.city}`;
  }

  public get kindTrad() {
    return 'POI.KIND.' + this.kind.toUpperCase();
  }

  public get isPoint() {
    return this.kind === 'point';
  }

  public get isPoly() {
    return this.kind === 'polygon';
  }

  public get isCircle() {
    return this.kind === 'circle';
  }

  public getPolyline() {
    return encode(Landmark.flipped(this.feature?.geometry?.coordinates?.[0]));
  }

  static flipped(coords) {
    const flipped = [];
    for (let i = 0; i < coords.length; i++) {
      const coord = coords[i].slice();
      flipped.push([coord[1], coord[0]]);
    }
    return flipped;
  }

  public get phoneNational(): string | null {
    return getFormatNational(this.phone);
  }

  public get phoneE164() {
    return getFormatE164(this.phone);
  }
}

export default Landmark;
